import CustomTransferList from 'Components/CustomTransferList/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import { useProducts } from 'Services/ProductManagement/Features/Product/Hooks'
import useTariffProducts from 'Services/ProductManagement/Features/Product/Hooks/useTariffProducts'
import type { Product } from 'Services/ProductManagement/Features/Product/productSlice'
import useSingleTariff from 'Services/ProductManagement/Features/Tariff/Hooks/useSingleTariff'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/tariffSlice'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import ListItemText from '@mui/material/ListItemText'
import { unwrapResult } from '@reduxjs/toolkit'
import type * as React from 'react'
import { useMemo, useState } from 'react'
import { t } from 'ttag'

interface TariffProductsEditProps {
  selectedTariff: Tariff
  productGroupOptions: { label: string; value: string }[] | undefined
  handleCancel: () => void
  handleSaveSuccess?: () => void
}

function TariffProductsEdit({
  selectedTariff,
  productGroupOptions,
  handleCancel,
  handleSaveSuccess
}: Readonly<TariffProductsEditProps>) {
  const { updateTariff, singleLoading } = useSingleTariff(undefined)
  const { allProducts, allProductsLoading } = useProducts()
  const { allTariffProducts, allTariffProductsLoading } = useTariffProducts(selectedTariff.id)

  const [searchText, setSearchText] = useState('')
  const [productGroupFilter, setProductGroupFilter] = useState('')

  const filterOptions = (optionsToFilter: Product[]) => {
    const optionsFilteredBySearch = filterBySearch(optionsToFilter)
    const optionsFilteredByProductGroup = filterByProductGroup(optionsFilteredBySearch)
    return optionsFilteredByProductGroup
  }

  /**
   * Filters the given array of products based on the search text.
   *
   * The function checks if the search text is present in either the product name's finnish translation
   * or the product code. If the search text is not provided, it returns the original
   * array of products.
   */
  const filterBySearch = (optionsToFilter: Product[]) => {
    if (!searchText) return optionsToFilter
    return optionsToFilter.filter(product => {
      const productName = getTranslation(product.translations, 'fi').name.toLowerCase()
      const productCode = product.product_code.toLowerCase()
      const searchTextLower = searchText.toLowerCase()
      return productName.includes(searchTextLower) || productCode.includes(searchTextLower)
    })
  }

  const filterByProductGroup = (optionsToFilter: Product[]) => {
    if (!productGroupFilter) return optionsToFilter
    return optionsToFilter.filter(product => product.product_group === productGroupFilter)
  }

  /**
   * Filters out the products that are already in the selected tariff.
   * Since we are making copies of the products, we can not use the product IDs directly.
   * Instead, we need to compare the product_code of the products.
   *
   * @returns Product[] - Array of products that are not already in the selected tariff.
   */
  const productsWithoutTariffProducts = useMemo(() => {
    return allProducts.filter(
      product =>
        !allTariffProducts.some(
          tariffProduct => tariffProduct.product_code === product.product_code
        )
    )
  }, [allProducts, allTariffProducts])

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value
    setProductGroupFilter(newValue)
  }

  /**
   * Handles saving the selected tariff products.
   *
   * @param {Product[]} selectedItems - The list of products that are currently selected for the tariff.
   */
  const handleSave = (selectedItems: Product[]) => {
    // Filter out the new items that are not already in the selected tariff's product IDs
    const newItems = selectedItems.filter(item => !selectedTariff.product_ids.includes(item.id))

    // Update the tariff with the newly added products
    updateTariff(selectedTariff.id, { added_products: newItems })
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Successfully saved changes.`)
        handleSaveSuccess?.()
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const renderItemContent = (product: Product) => {
    const labelId = `transfer-list-item-${product.id}-label`
    return (
      <ListItemText
        id={labelId}
        primary={`${getTranslation(product?.translations, 'fi').name}`}
        secondary={product?.product_code}
      />
    )
  }

  const isLoading =
    allProductsLoading === 'pending' ||
    allTariffProductsLoading === 'pending' ||
    singleLoading === 'pending'

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl sx={{ width: '48%' }}>
          <TextField
            size="small"
            sx={{ backgroundColor: 'background.default' }}
            label={t`Product search`}
            placeholder={t`Search products`}
            value={searchText}
            onChange={handleSearchTextChange}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </FormControl>
        <FormControl sx={{ width: '48%' }}>
          <InputLabel shrink id="product-group-select-label">{t`Product group`}</InputLabel>
          <Select
            name="product_group"
            labelId="product-group-select-label"
            value={productGroupFilter}
            size="small"
            displayEmpty
            label={t`Product group`}
            onChange={handleChange}
            sx={{ backgroundColor: 'background.default' }}
            input={<OutlinedInput notched label={t`Product group`} />}
          >
            {productGroupOptions?.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomTransferList<Product>
        leftItemsHeaderText={t`Product listing`}
        rightItemsHeaderText={t`Tariff products`}
        leftItems={productsWithoutTariffProducts}
        rightItems={allTariffProducts}
        renderItemContent={renderItemContent}
        handleSave={handleSave}
        handleCancel={handleCancel}
        allowTransferBack={false}
        compareFn={(item: Product) => item.id}
        filterFn={filterOptions}
      />
    </WithLoader>
  )
}

export default TariffProductsEdit
